// @givmo/payments — `useShift4` React hook.
//
// Wraps the imperative `createShift4` loader (CDN script + factory) in a render-safe hook:
// loads Shift4.js on mount, instantiates a `Shift4` for the given publishable key, and
// exposes the instance plus a load status. Re-instantiates when the key changes (e.g. the
// config endpoint rotates the key). SSR-safe: the effect only runs in the browser.
import { useEffect, useRef, useState } from 'react';
import { SHIFT4_JS_SRC, createShift4, type Shift4LoadStatus } from './script-loader';
import type { Shift4Instance } from './shift4-types';

export interface UseShift4Result {
  /** The instantiated `Shift4` object, or `null` until ready. */
  shift4: Shift4Instance | null;
  status: Shift4LoadStatus;
  /** The load error, if `status === 'error'`. */
  error: Error | null;
}

/**
 * Load Shift4.js and instantiate it for `publishableKey`.
 *
 * Pass `null`/`undefined` for the key to defer loading until it has been sourced from the
 * config endpoint (the hook stays `idle` until a key is supplied). The publishable key is
 * the only secret-shaped input and it is processor config, never baked into this package.
 */
export function useShift4(
  publishableKey: string | null | undefined,
  src: string = SHIFT4_JS_SRC,
): UseShift4Result {
  const [shift4, setShift4] = useState<Shift4Instance | null>(null);
  const [status, setStatus] = useState<Shift4LoadStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  // Guard against state updates after unmount / key change racing the async load.
  const activeKey = useRef<string | null>(null);

  useEffect(() => {
    if (!publishableKey) {
      activeKey.current = null;
      setShift4(null);
      setStatus('idle');
      setError(null);
      return;
    }

    let cancelled = false;
    activeKey.current = publishableKey;
    setStatus('loading');
    setError(null);

    createShift4(publishableKey, src)
      .then((instance) => {
        if (cancelled || activeKey.current !== publishableKey) return;
        setShift4(instance);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled || activeKey.current !== publishableKey) return;
        setShift4(null);
        setStatus('error');
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => {
      cancelled = true;
    };
  }, [publishableKey, src]);

  return { shift4, status, error };
}
